import algoliasearch from 'algoliasearch/lite';
import instantsearch from 'instantsearch.js';
import { connectSearchBox, connectHits, connectStats } from 'instantsearch.js/es/connectors';
import { configure } from 'instantsearch.js/es/widgets';
import BaseComponent from 'scripts/BaseComponent.js';

export default class ZipcodeSearch extends BaseComponent {
  constructor(component) {
    super(component);
    this.debug = false;

    this.refs = {
      algoliaSettingsJson: '.js-algolia-settings-json',
      container: '.js-zipcodeFormContainer',
      input: '.js-zipcodeSearchInput',
      hitsContainer: '.js-zipCodeHitsContainer',
      resultsCount: '.js-zipCodeResultsCount',
      option: '.js-zipcodeOption',
      submit: '.js-zipcodeSubmit',
    };

    this.dom = {
      $component: component,
      $algoliaSettingsJson: document.querySelector(this.refs.algoliaSettingsJson),
      $container: this.$get(this.refs.container),
      $input: this.$get(this.refs.input),
      $hitsContainer: this.$get(this.refs.hitsContainer),
      $resultsCount: this.$get(this.refs.resultsCount),
      $submit: this.$get(this.refs.submit),
    };

    this.state = {
      search: null,
      initialized: false,
      hasResults: false,
      resultsExpanded: false,
      activeSearchClass: 'is-active',
      emptySearchClass: 'is-empty',
      activeOptionClass: 'is-active-option',
      zipcodeSelectedEvent: 'zipcodeselected',
      emptySearchEvent: 'emptysearch',
    };

    this.init();
    this.mount();
  }

  init() {
    const { dom, state } = this;
    if (!dom.$algoliaSettingsJson) return;

    const { APP_ID, KEY } = this.getAlgoliaSettings();
    const algoliaClient = algoliasearch(APP_ID, KEY);
    const searchClient = {
      ...algoliaClient,
      search: (requests) => this.searchProxy(requests, algoliaClient),
    };

    state.search = instantsearch({
      searchClient,
      indexName: 'concierge_zip_codes',
    });

    state.initialized = true;
  }

  mount() {
    const { dom } = this;
    super.mount();

    this.addSearchBox();
    this.addHitsContainer();
    this.addStatus();
    this.start();

    this.setClick(dom.$submit, this.handleEnterPress);
  }

  getAlgoliaSettings() {
    const { dom } = this;
    return JSON.parse(dom.$algoliaSettingsJson.innerHTML);
  }

  searchProxy(requests, algoliaClient) {
    const hasNoQuery = requests.every(({ params }) => !params.query);

    if (hasNoQuery) {
      return Promise.resolve({
        results: requests.map(() => ({
          hits: [],
          nbHits: 0,
          nbPages: 0,
          page: 0,
          processingTimeMS: 0,
          hitsPerPage: 0,
          exhaustiveNbHits: false,
          query: '',
          params: '',
        })),
      });
    }

    return algoliaClient.search(requests);
  }

  renderSearchBox({ refine }, isFirstRender) {
    const { dom } = this;
    if (!isFirstRender) return;

    this.setOnEvent('keyup', dom.$input, (e) => this.handleInputKeyUp(e, refine));
    this.setOnEventWithElement('focus', dom.$input, this.handleInputFocus);
  }

  addSearchBox() {
    const { state } = this;

    const customSearchBox = connectSearchBox(this.renderSearchBox.bind(this));
    state.search.addWidgets([customSearchBox()]);
  }

  renderHits({ hits }) {
    const { dom } = this;

    dom.$hitsContainer.innerHTML = '';
    hits.forEach((hit, i) => dom.$hitsContainer.appendChild(this.renderHit(hit, i)));
  }

  renderHit(hit, i) {
    const zipCode = hit['Zip Code'];

    const option = document.createElement('li');
    option.classList = 'ZipcodeSearch__zipcode-result body js-zipcodeOption';
    option.setAttribute('role', 'option');
    option.setAttribute('id', `zipcode_result_${i}`);
    option.setAttribute('data-coast', hit.Coast);
    option.setAttribute('data-zipcode', zipCode);
    option.innerHTML = zipCode;

    this.setClickWithElement(option, this.handleOptionClick);

    return option;
  }

  addHitsContainer() {
    const { state } = this;

    const customHits = connectHits(this.renderHits.bind(this));

    state.search.addWidgets([
      configure({
        hitsPerPage: 7,
      }),
      customHits(),
    ]);
  }

  renderStatus({ nbHits, query }) {
    const { dom, state } = this;

    this.setResultsCountText(nbHits, query);

    if (!query) {
      this.collapseResults();
      dom.$component.classList.remove(state.emptySearchClass);
      state.hasResults = false;
      return;
    }

    if (nbHits === 0) {
      dom.$component.classList.add(state.emptySearchClass);
      this.dispatch(state.emptySearchEvent);
      state.hasResults = false;
      this.collapseResults();
    } else {
      dom.$component.classList.remove(state.emptySearchClass);
      state.hasResults = true;
      this.expandResults();
    }
  }

  setResultsCountText(nbHits, query) {
    const { dom } = this;

    if (!query) {
      dom.$resultsCount.innerHTML = '';
      return;
    }

    if (nbHits === 0) {
      const { emptyMessage } = dom.$resultsCount.dataset;
      dom.$resultsCount.innerHTML = emptyMessage;
    } else {
      dom.$resultsCount.innerHTML = `${nbHits} zip codes found`;
    }
  }

  expandResults() {
    const { dom, state } = this;

    dom.$component.classList.add(state.activeSearchClass);
    dom.$input.setAttribute('aria-expanded', true);

    document.addEventListener('click', this.handleBodyClick.bind(this));

    state.resultsExpanded = true;
  }

  collapseResults() {
    const { dom, state } = this;

    dom.$component.classList.remove(state.activeSearchClass);
    dom.$input.setAttribute('aria-expanded', false);
    dom.$input.removeAttribute('aria-activedescendant');

    document.removeEventListener('click', this.handleBodyClick.bind(this));

    state.resultsExpanded = false;
  }

  addStatus() {
    const { state } = this;

    const customStatus = connectStats(this.renderStatus.bind(this));
    state.search.addWidgets([customStatus()]);
  }

  handleInputKeyUp(e, refine) {
    const { dom, state: { resultsExpanded } } = this;

    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        if (resultsExpanded) this.highlightPreviousOption();
        break;

      case 'ArrowDown':
        e.preventDefault();
        if (resultsExpanded) this.highlightNextOption();
        break;

      case 'Escape':
        if (resultsExpanded) this.collapseResults();
        break;

      case 'Enter':
        this.handleEnterPress();
        break;

      default:
        refine(dom.$input.value);
        break;
    }
  }

  handleInputFocus(input) {
    const { state: { hasResults } } = this;

    if (input.value && hasResults) {
      this.expandResults();
    }
  }

  handleBodyClick(e) {
    const { dom, state } = this;

    if (!dom.$container.contains(e.target) && state.resultsExpanded) {
      this.collapseResults();
    }
  }

  removeOptionHighlight() {
    const { state, refs } = this;
    const options = this.$getAll(refs.option);

    options.forEach((option) => option.classList.remove(state.activeOptionClass));
  }

  highlightOption(option) {
    const { dom, state } = this;
    const id = option.getAttribute('id');

    dom.$input.setAttribute('aria-activedescendant', id);
    option.classList.add(state.activeOptionClass);
  }

  highlightPreviousOption() {
    const currentOption = this.getCurrentOption();
    const prevOption = this.getFollowingOption(currentOption, 'prev');
    if (!prevOption) return;

    this.removeOptionHighlight();
    this.highlightOption(prevOption);
  }

  highlightNextOption() {
    const currentOption = this.getCurrentOption();
    const nextOption = this.getFollowingOption(currentOption, 'next');
    if (!nextOption) return;

    this.removeOptionHighlight();
    this.highlightOption(nextOption);
  }

  getFollowingOption(currentOption, direction) {
    const isPrev = direction === 'prev';

    if (!currentOption) {
      return isPrev ? this.getLastOption() : this.getFirstOption();
    }

    const directionProp = isPrev ? 'previousElementSibling' : 'nextElementSibling';
    return currentOption[directionProp];
  }

  getCurrentOption() {
    const { state, refs } = this;
    return this.$get(`${refs.option}.${state.activeOptionClass}`);
  }

  getFirstOption() {
    const { refs } = this;
    return this.$get(refs.option);
  }

  getLastOption() {
    const { refs } = this;
    const options = this.$getAll(refs.option);

    return options[options.length - 1];
  }

  handleEnterPress() {
    const { dom, state, refs } = this;

    const currentOption = this.getCurrentOption();
    if (currentOption) {
      this.handleOptionClick(currentOption);
      return;
    }

    const matchingOption = this.$get(`${refs.option}[data-zipcode="${dom.$input.value}"]`);
    if (!matchingOption) {
      if (!dom.$input.value) return;

      const { emptyMessage } = dom.$resultsCount.dataset;
      dom.$resultsCount.innerHTML = emptyMessage;
      dom.$component.classList.add(state.emptySearchClass);
      this.dispatch(state.emptySearchEvent);
      this.collapseResults();
    } else {
      this.handleOptionClick(matchingOption);
    }
  }

  handleOptionClick(option) {
    const { dom, state } = this;
    const { zipcode, coast } = option.dataset;

    dom.$input.value = zipcode;
    this.collapseResults();

    this.dispatch(state.zipcodeSelectedEvent, { zipcode, coast });
  }

  start() {
    const { state } = this;
    if (!state.initialized) return;

    state.search.start();
  }
}
